import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import CircularProgress from "@/components/common/CircularProgress";
import { comunidadAdapter, FormacionDevolucionAdmin } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { DevolucionPuntaje } from "./DevolucionPuntaje";

const RESUMEN_MAX = 2000;
/** Con menos calificaciones, quien dictó podría deducir quién dijo qué: se le avisa a quien revisa. */
const MIN_CALIFICACIONES_ANONIMA = 3;

const fechaHora = (iso: string) =>
  new Date(iso).toLocaleString("es-AR", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "America/Argentina/Buenos_Aires",
  });

const nombres = (docentes: FormacionDevolucionAdmin["docentes"]) =>
  docentes.map((d) => `${d.name} ${d.surname}`.trim()).join(" y ");

interface FormacionDevolucionPanelProps {
  formacionKey: string;
}

/**
 * Panel de admin: devolución para quien dictó la formación. Se arma sola (puntaje + resumen con
 * IA, anónimo y juntando las dos ediciones) el 1° del mes siguiente a las 9:00, cuando ya cerró
 * "Calificá". Acá se revisa, se corrige el resumen si hace falta y se confirma: al confirmar se
 * publica en "Devoluciones" de quien dictó y le llega el aviso.
 */
export const FormacionDevolucionPanel = ({ formacionKey }: FormacionDevolucionPanelProps) => {
  const [devolucion, setDevolucion] = useState<FormacionDevolucionAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [resumen, setResumen] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    comunidadAdapter
      .getDevolucionAdmin(formacionKey)
      .then((d) => {
        if (cancelled) return;
        setDevolucion(d);
        setResumen(d.resumen);
      })
      .catch(() => { if (!cancelled) setDevolucion(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [formacionKey]);

  const cambiado = devolucion !== null && resumen.trim() !== devolucion.resumen.trim();

  const guardar = async () => {
    setSaving(true);
    try {
      const d = await comunidadAdapter.saveDevolucion(formacionKey, resumen.trim());
      setDevolucion(d);
      setResumen(d.resumen);
      toastUtils.success("Cambios guardados. Todavía no se publicó.");
    } catch {
      toastUtils.error("No se pudieron guardar los cambios. Probá de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const publicar = async () => {
    if (!devolucion) return;
    const quien = nombres(devolucion.docentes);
    const ok = window.confirm(
      `Se publica en "Devoluciones" de ${quien} y le llega un aviso (campanita y celular). Después no se puede editar. ¿Confirmar?`,
    );
    if (!ok) return;
    setSaving(true);
    try {
      const d = await comunidadAdapter.publishDevolucion(formacionKey, resumen.trim());
      setDevolucion(d);
      setResumen(d.resumen);
      toastUtils.success(`Listo: ${quien} ya puede ver su devolución.`);
    } catch {
      toastUtils.error("No se pudo publicar. Probá de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#E3D9FF] p-4" data-devolucion-panel>
      <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Devolución para quien la dictó</span>

      {loading ? (
        <div className="flex justify-center py-3">
          <CircularProgress />
        </div>
      ) : !devolucion ? (
        <p className="m-0 text-[13px] text-gray-500">No se pudo cargar la devolución. Probá de nuevo en un rato.</p>
      ) : devolucion.docentes.length === 0 ? (
        <p className="m-0 text-[13px] text-gray-500">
          Cargá «¿Quién la dicta?» en el calendario para que la devolución tenga a quién llegarle.
        </p>
      ) : devolucion.status === "pendiente" ? (
        <p className="m-0 text-[13px] text-gray-500">
          «Calificá» sigue abierto. La devolución para {nombres(devolucion.docentes)} se arma sola el{" "}
          <span className="font-semibold text-[#4A3D73]">{fechaHora(devolucion.generaEl)}</span>, y te aparece acá para
          revisarla.
        </p>
      ) : devolucion.status === "publicada" ? (
        <div className="flex flex-col gap-2">
          {devolucion.puntaje !== null && <DevolucionPuntaje puntaje={devolucion.puntaje} />}
          <p className="m-0 whitespace-pre-line text-[13.5px] leading-relaxed text-[#4A3D73]">{devolucion.resumen}</p>
          <span className="text-[12.5px] font-semibold text-emerald-700">
            ✓ Publicada el {devolucion.publishedAt ? fechaHora(devolucion.publishedAt) : ""} — {nombres(devolucion.docentes)} ya
            la puede ver.
          </span>
        </div>
      ) : devolucion.calificaciones === 0 ? (
        <p className="m-0 text-[13px] text-gray-500">
          Nadie calificó esta formación, así que no hay devolución para mandarle a {nombres(devolucion.docentes)}.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-[13px] text-gray-500">
            Lista para revisar. Es lo que va a ver {nombres(devolucion.docentes)}: anónima y juntando las dos ediciones.
          </p>
          {devolucion.puntaje !== null && (
            <div className="flex flex-col gap-0.5">
              <DevolucionPuntaje puntaje={devolucion.puntaje} />
              <span className="text-[12.5px] text-gray-500">
                Promedio de {devolucion.calificaciones}{" "}
                {devolucion.calificaciones === 1 ? "calificación" : "calificaciones"}
              </span>
            </div>
          )}
          {devolucion.calificaciones < MIN_CALIFICACIONES_ANONIMA && (
            <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] leading-snug text-amber-800">
              Ojo: calificaron solo {devolucion.calificaciones === 1 ? "1 persona" : `${devolucion.calificaciones} personas`}. Con tan
              pocas, {nombres(devolucion.docentes)} podría darse cuenta de quién dijo qué. Podés publicarla igual o no publicarla.
            </p>
          )}
          <label htmlFor={`devolucion-resumen-${formacionKey}`} className="flex items-center gap-1.5 text-[13px] font-semibold text-[#2D2049]">
            <Sparkles size={14} className="text-[#8475B3]" />
            Resumen armado con IA — corregilo si hace falta
          </label>
          {!devolucion.resumen && (
            <p className="m-0 text-[12.5px] text-amber-700">
              Esta vez la IA no pudo armar el resumen. Escribilo vos con lo que dejaron en «Calificaciones».
            </p>
          )}
          <Textarea
            id={`devolucion-resumen-${formacionKey}`}
            value={resumen}
            onChange={(e) => setResumen(e.target.value.slice(0, RESUMEN_MAX))}
            rows={7}
            className="text-[13.5px] leading-relaxed"
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {cambiado && (
              <Button variant="outline" size="sm" className="border-[#C9BCE8] text-[#665090]" disabled={saving} onClick={guardar}>
                Guardar sin publicar
              </Button>
            )}
            <Button size="sm" className="bg-[#6D57BC] hover:bg-[#6D57BC]/90" disabled={saving || !resumen.trim()} onClick={publicar}>
              {saving ? "Guardando…" : "Confirmar y publicar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormacionDevolucionPanel;
